import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, unref as _unref, mergeProps as _mergeProps, renderList as _renderList, Fragment as _Fragment } from "vue";
const _hoisted_1 = {
    key: 3,
    class: "mb-4"
};
const _hoisted_2 = { class: "text-body-2 text-blue-gray mt-2" };
const _hoisted_3 = ["title"];
const _hoisted_4 = {
    key: 1,
    class: "text-blue-gray"
};
const _hoisted_5 = { class: "text-body-2 text-blue-gray" };
import { computed, onMounted, ref, watch } from "vue";
import { usePolling } from "@/composables/usePolling";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { carequalityService, IN_FLIGHT_STATUSES } from "@/services/carequalityService";
import { isoToMmddyyyy } from "@/utils/dateUtils";
import { formatFileSize, pluralize } from "@/utils";
const POLL_INTERVAL_MS = 5000;
const INSIGHTS_POLL_TICKS = 60;
export default /*@__PURE__*/ _defineComponent({
    __name: 'NetworkRecordsDialog',
    props: {
        modelValue: { type: Boolean },
        orderId: {},
        patientId: {}
    },
    emits: ["update:modelValue", "selection-saved", "state-changed"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const KIND_LABELS = { raw: "Record", mr_summary: "Summary" };
        const requirementsStore = useRequirementsStore();
        const maxSelection = computed(() => requirementsStore.clientConfig?.intake?.carequalityNetworkDocumentsMax ?? 10);
        const headers = [
            { title: "Date", key: "documentDate" },
            { title: "Source", key: "sourceOrganization" },
            { title: "Classification", key: "classification" },
            { title: "Summary", key: "summary", sortable: false },
            { title: "Policy Criteria", key: "policyReviewMatches", sortable: false },
            { title: "Size", key: "sizeBytes" },
            { title: "", key: "preview", sortable: false, align: "end" },
        ];
        const run = ref(null);
        const records = ref([]);
        const selectedIds = ref([]);
        const search = ref("");
        const loading = ref(false);
        const saving = ref(false);
        const triggering = ref(false);
        const matching = ref(false);
        const previewingId = ref(null);
        const errorMsg = ref("");
        const noticeMsg = ref("");
        const autoOpenPending = ref(false);
        const insightsPollTicks = ref(0);
        const insightsWindowActive = computed(() => insightsPollTicks.value > 0);
        let latestSaveIssued = 0;
        let savesInFlight = 0;
        let saveQueue = Promise.resolve();
        const isInFlight = computed(() => !!run.value && IN_FLIGHT_STATUSES.includes(run.value.status));
        const isFailed = computed(() => run.value?.status === "FAILED");
        // Only a run that finished and found nothing is empty. A load that failed, or one still to start,
        // leaves the reader with the error or the progress bar rather than a false "the network has nothing".
        const isEmpty = computed(() => !!run.value && !isInFlight.value && !isFailed.value && !records.value.length);
        const failureText = computed(() => run.value?.errorText || "The network search did not complete. Try searching again.");
        /** Share of the vendor's work that is done, or null while it reports no counts to divide. */
        const reportedPercent = computed(() => {
            const phases = Object.values(run.value?.phaseProgress ?? {});
            const total = phases.reduce((sum, phase) => sum + (phase.total ?? 0), 0);
            if (!total)
                return null;
            const done = phases.reduce((sum, phase) => sum + (phase.successful ?? 0), 0);
            return Math.min(100, Math.round((done / total) * 100));
        });
        const progressPercent = ref(null);
        watch(() => run.value?.id, () => (progressPercent.value = null));
        watch(reportedPercent, (value) => {
            if (value !== null)
                progressPercent.value = Math.max(progressPercent.value ?? 0, value);
        }, { immediate: true });
        const progressLabel = computed(() => {
            const phases = run.value?.phaseProgress ?? {};
            const total = phases.download?.total ?? null;
            if (!total)
                return "Searching the network for this patient's records.";
            const converting = phases.convert?.total ?? null;
            const counts = `${records.value.length} of ${total} available`;
            if (!converting)
                return `${counts}. Records appear here as they arrive.`;
            const converted = phases.convert?.successful ?? 0;
            return `${counts}, ${converted} of ${converting} converted. Converting C-CDA XML records into a Medical Summary document.`;
        });
        const isSummary = (record) => record.kind === "mr_summary";
        const sourceLabel = (record) => record.sourceOrganization.trim() || "Unknown source";
        function describeRecord(record) {
            const documentDate = isoToMmddyyyy(record.documentDate) || "Not dated";
            if (isSummary(record))
                return `the medical record summary from ${documentDate}`;
            return `${sourceLabel(record)} — ${documentDate}`;
        }
        watch([isInFlight, records, saving], () => {
            emit("state-changed", { inFlight: isInFlight.value, recordCount: records.value.length, saving: saving.value });
        });
        function describe(ex) {
            return ex instanceof Error ? ex.message : String(ex);
        }
        async function readRecords() {
            const state = await carequalityService.getNetworkQuery(props.patientId, props.orderId);
            run.value = state.run;
            records.value = state.documents;
        }
        // Polling tracks the pursuits, not the dialog: a reviewer who closes the dialog still gets the
        // button badge updated. A terminal run can still receive its medical-record summary later, and a
        // queued insights pass has a bounded window of its own, so polling continues until every pursuit
        // settles. The insights budget is only spent on ticks no other pursuit is already paying for.
        async function pollRecords() {
            const runActive = !!run.value && isInFlight.value;
            const summaryPending = !!run.value?.summaryPending;
            if (!runActive && !summaryPending && insightsPollTicks.value <= 0)
                return;
            if (insightsPollTicks.value > 0 && !runActive && !summaryPending)
                insightsPollTicks.value -= 1;
            try {
                await readRecords();
            }
            catch (ex) {
                errorMsg.value = `Error refreshing network records: ${describe(ex)}`;
            }
            settleAutoOpen();
        }
        /** The search the button started shows itself once it has records, or once it settles without any. */
        function settleAutoOpen() {
            if (!autoOpenPending.value)
                return;
            if (!records.value.length && isInFlight.value)
                return;
            autoOpenPending.value = false;
            emit("update:modelValue", true);
        }
        const { refresh } = usePolling(pollRecords, { interval: POLL_INTERVAL_MS });
        async function triggerQuery() {
            triggering.value = true;
            const displayed = run.value;
            try {
                const { run: started } = await carequalityService.triggerNetworkQuery(props.patientId, props.orderId);
                if (displayed && started.id === displayed.id && !IN_FLIGHT_STATUSES.includes(started.status)) {
                    noticeMsg.value = "A search ran moments ago; the records below are current.";
                }
                run.value = started;
                refresh();
            }
            catch (ex) {
                errorMsg.value = `Error starting the network search: ${describe(ex)}`;
            }
            finally {
                triggering.value = false;
            }
        }
        async function open() {
            search.value = "";
            loading.value = true;
            try {
                const [state, selection] = await Promise.all([
                    carequalityService.getNetworkQuery(props.patientId, props.orderId),
                    carequalityService.getSelection(props.orderId),
                ]);
                run.value = state.run;
                records.value = state.documents;
                selectedIds.value = selection.documentIds;
            }
            catch (ex) {
                errorMsg.value = `Error loading network records: ${describe(ex)}`;
            }
            finally {
                loading.value = false;
            }
        }
        async function onSearchAgain() {
            errorMsg.value = "";
            noticeMsg.value = "";
            await triggerQuery();
        }
        async function onRunInsights() {
            matching.value = true;
            errorMsg.value = "";
            noticeMsg.value = "";
            try {
                const { queued } = await carequalityService.runInsights(props.patientId, props.orderId);
                noticeMsg.value = queued
                    ? `Running insights on ${queued} ${pluralize(queued, "record")}: classification, summary, and this order's policy criteria.`
                    : "No stored records to analyze yet.";
                insightsPollTicks.value = INSIGHTS_POLL_TICKS;
                refresh();
            }
            catch (ex) {
                errorMsg.value = `Error starting insights: ${describe(ex)}`;
            }
            finally {
                matching.value = false;
            }
        }
        /** The trigger, called from the button that owns this dialog: search first, show the dialog later. */
        async function startSearch() {
            errorMsg.value = "";
            noticeMsg.value = "";
            autoOpenPending.value = true;
            await triggerQuery();
            settleAutoOpen();
        }
        async function onSelectionChange(ids) {
            const previous = selectedIds.value;
            // The server enforces the same ceiling; catching it here keeps the reviewer's tick from appearing
            // and vanishing behind a 400 they cannot act on.
            if (ids.length > maxSelection.value) {
                noticeMsg.value = `Attach at most ${maxSelection.value} records to one prior authorization.`;
                selectedIds.value = [...previous];
                return;
            }
            noticeMsg.value = "";
            // The table accepts a click while a save is open. Saves are chained so the server commits them
            // in click order; only the newest write paints the checkboxes and the badge.
            const issued = ++latestSaveIssued;
            savesInFlight += 1;
            selectedIds.value = ids;
            saving.value = true;
            errorMsg.value = "";
            const request = saveQueue.then(() => carequalityService.saveSelection(props.orderId, ids));
            saveQueue = request.catch(() => undefined);
            try {
                const saved = await request;
                if (issued !== latestSaveIssued)
                    return;
                // The stored set, not the clicked one: a rejected id must not stay checked in the UI.
                selectedIds.value = saved.documentIds;
                emit("selection-saved", saved.documentIds.length);
                // The server stores what it can and reports the rest by omission; a shrunk reply must not
                // read as success, so name it instead of letting the tick silently vanish.
                if (saved.documentIds.length !== ids.length) {
                    errorMsg.value = "Some records could not be attached. The list shows what was saved.";
                }
            }
            catch (ex) {
                if (issued !== latestSaveIssued)
                    return;
                errorMsg.value = `Error saving the selection: ${describe(ex)}`;
                try {
                    const persisted = await carequalityService.getSelection(props.orderId);
                    if (issued !== latestSaveIssued)
                        return;
                    selectedIds.value = persisted.documentIds;
                    emit("selection-saved", persisted.documentIds.length);
                }
                catch {
                    if (issued !== latestSaveIssued)
                        return;
                    selectedIds.value = previous;
                }
            }
            finally {
                savesInFlight -= 1;
                saving.value = savesInFlight > 0;
            }
        }
        async function onPreview(record) {
            if (previewingId.value !== null)
                return;
            const win = window.open("", "_blank");
            previewingId.value = record.id;
            errorMsg.value = "";
            try {
                const { url } = await carequalityService.getDocumentLink(props.patientId, record.id);
                if (win)
                    win.location.replace(url);
                else
                    window.open(url, "_blank", "noopener");
            }
            catch (ex) {
                win?.close();
                errorMsg.value = `Error opening the record: ${describe(ex)}`;
            }
            finally {
                previewingId.value = null;
            }
        }
        function onDialogToggle(value) {
            emit("update:modelValue", value);
        }
        watch(() => props.modelValue, (isOpen) => {
            if (isOpen) {
                open();
                return;
            }
            errorMsg.value = "";
            noticeMsg.value = "";
        });
        onMounted(async () => {
            try {
                const [, selection] = await Promise.all([readRecords(), carequalityService.getSelection(props.orderId)]);
                selectedIds.value = selection.documentIds;
                emit("selection-saved", selection.documentIds.length);
            }
            catch {
                // A failed first read leaves the button unbadged; opening the dialog surfaces the error.
            }
        });
        __expose({ startSearch });
        return (_ctx, _cache) => {
            const _component_v_spacer = _resolveComponent("v-spacer");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_card_title = _resolveComponent("v-card-title");
            const _component_v_alert = _resolveComponent("v-alert");
            const _component_v_progress_linear = _resolveComponent("v-progress-linear");
            const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
            const _component_v_text_field = _resolveComponent("v-text-field");
            const _component_v_chip = _resolveComponent("v-chip");
            const _component_v_tooltip = _resolveComponent("v-tooltip");
            const _component_v_data_table = _resolveComponent("v-data-table");
            const _component_v_card_text = _resolveComponent("v-card-text");
            const _component_v_btn_lh = _resolveComponent("v-btn-lh");
            const _component_v_card_actions = _resolveComponent("v-card-actions");
            const _component_v_card = _resolveComponent("v-card");
            const _component_v_dialog = _resolveComponent("v-dialog");
            return (_openBlock(), _createBlock(_component_v_dialog, {
                "model-value": __props.modelValue,
                "max-width": "1100",
                scrollable: "",
                "onUpdate:modelValue": onDialogToggle
            }, {
                default: _withCtx(() => [
                    _createVNode(_component_v_card, null, {
                        default: _withCtx(() => [
                            _createVNode(_component_v_card_title, { class: "d-flex align-center ga-3 py-4" }, {
                                default: _withCtx(() => [
                                    _cache[5] || (_cache[5] = _createElementVNode("span", { class: "text-h6" }, "Network Records", -1)),
                                    _createVNode(_component_v_spacer),
                                    _createVNode(_component_v_btn, {
                                        variant: "tonal",
                                        color: "primary",
                                        size: "small",
                                        "prepend-icon": "mdi-clipboard-check-outline",
                                        loading: matching.value,
                                        disabled: insightsWindowActive.value,
                                        onClick: onRunInsights
                                    }, {
                                        default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                                                _createTextVNode(" Run insights ", -1)
                                            ]))]),
                                        _: 1
                                    }, 8, ["loading", "disabled"]),
                                    _createVNode(_component_v_btn, {
                                        variant: "tonal",
                                        color: "primary",
                                        size: "small",
                                        "prepend-icon": "mdi-refresh",
                                        loading: isInFlight.value || triggering.value,
                                        onClick: onSearchAgain
                                    }, {
                                        default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                                                _createTextVNode(" Search again ", -1)
                                            ]))]),
                                        _: 1
                                    }, 8, ["loading"]),
                                    _createVNode(_component_v_btn, {
                                        icon: "mdi-close",
                                        variant: "text",
                                        size: "small",
                                        "aria-label": "Close network records",
                                        onClick: _cache[0] || (_cache[0] = ($event) => (emit('update:modelValue', false)))
                                    })
                                ]),
                                _: 1
                            }),
                            _createVNode(_component_v_card_text, null, {
                                default: _withCtx(() => [
                                    (errorMsg.value)
                                        ? (_openBlock(), _createBlock(_component_v_alert, {
                                            key: 0,
                                            type: "error",
                                            variant: "tonal",
                                            density: "compact",
                                            class: "mb-4",
                                            text: errorMsg.value
                                        }, null, 8, ["text"]))
                                        : _createCommentVNode("", true),
                                    (noticeMsg.value)
                                        ? (_openBlock(), _createBlock(_component_v_alert, {
                                            key: 1,
                                            type: "info",
                                            variant: "tonal",
                                            density: "compact",
                                            class: "mb-4",
                                            text: noticeMsg.value
                                        }, null, 8, ["text"]))
                                        : _createCommentVNode("", true),
                                    (isFailed.value)
                                        ? (_openBlock(), _createBlock(_component_v_alert, {
                                            key: 2,
                                            type: "error",
                                            variant: "tonal",
                                            density: "compact",
                                            class: "mb-4",
                                            text: failureText.value
                                        }, null, 8, ["text"]))
                                        : _createCommentVNode("", true),
                                    (isInFlight.value)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                                            _createVNode(_component_v_progress_linear, {
                                                color: "accent",
                                                rounded: "",
                                                height: "6",
                                                indeterminate: progressPercent.value === null,
                                                "model-value": progressPercent.value ?? 0
                                            }, null, 8, ["indeterminate", "model-value"]),
                                            _createElementVNode("p", _hoisted_2, _toDisplayString(progressLabel.value), 1)
                                        ]))
                                        : _createCommentVNode("", true),
                                    (loading.value && !records.value.length)
                                        ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                                            key: 4,
                                            type: "table-row@4"
                                        }))
                                        : (records.value.length)
                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                                                _createVNode(_component_v_text_field, {
                                                    modelValue: search.value,
                                                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((search).value = $event)),
                                                    "prepend-inner-icon": "mdi-magnify",
                                                    placeholder: "Filter records",
                                                    clearable: "",
                                                    "hide-details": "",
                                                    density: "comfortable",
                                                    class: "mb-4"
                                                }, null, 8, ["modelValue"]),
                                                _createVNode(_component_v_data_table, {
                                                    "model-value": selectedIds.value,
                                                    headers: headers,
                                                    items: records.value,
                                                    search: search.value,
                                                    loading: saving.value,
                                                    "item-value": "id",
                                                    "show-select": "",
                                                    density: "comfortable",
                                                    "items-per-page": 25,
                                                    "onUpdate:modelValue": onSelectionChange
                                                }, {
                                                    [`item.documentDate`]: _withCtx(({ item }) => [
                                                        _createTextVNode(_toDisplayString(_unref(isoToMmddyyyy)(item.documentDate) || "Not dated"), 1)
                                                    ]),
                                                    [`item.sourceOrganization`]: _withCtx(({ item }) => [
                                                        _createTextVNode(_toDisplayString(isSummary(item) ? "Carequality network" : sourceLabel(item)), 1)
                                                    ]),
                                                    [`item.classification`]: _withCtx(({ item }) => [
                                                        _createVNode(_component_v_chip, {
                                                            size: "small",
                                                            rounded: "sm",
                                                            color: "blue-gray",
                                                            variant: "tonal"
                                                        }, {
                                                            default: _withCtx(() => [
                                                                _createTextVNode(_toDisplayString(item.classification || KIND_LABELS[item.kind]), 1)
                                                            ]),
                                                            _: 2
                                                        }, 1024)
                                                    ]),
                                                    [`item.summary`]: _withCtx(({ item }) => [
                                                        _createElementVNode("span", {
                                                            class: "network-records__summary",
                                                            title: item.summary
                                                        }, _toDisplayString(item.summary), 9, _hoisted_3)
                                                    ]),
                                                    [`item.policyReviewMatches`]: _withCtx(({ item }) => [
                                                        (item.policyReviewMatches)
                                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                                (item.policyReviewMatches.length)
                                                                    ? (_openBlock(), _createBlock(_component_v_tooltip, {
                                                                        key: 0,
                                                                        location: "top",
                                                                        "max-width": "480"
                                                                    }, {
                                                                        activator: _withCtx(({ props: tooltipProps }) => [
                                                                            _createVNode(_component_v_chip, _mergeProps(tooltipProps, {
                                                                                size: "small",
                                                                                rounded: "sm",
                                                                                color: "success",
                                                                                variant: "tonal",
                                                                                "prepend-icon": "mdi-check"
                                                                            }), {
                                                                                default: _withCtx(() => [
                                                                                    _createTextVNode(_toDisplayString(item.policyReviewMatches.length), 1)
                                                                                ]),
                                                                                _: 2
                                                                            }, 1040)
                                                                        ]),
                                                                        default: _withCtx(() => [
                                                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item.policyReviewMatches, (match) => {
                                                                                return (_openBlock(), _createElementBlock("div", {
                                                                                    key: match.id
                                                                                }, "• " + _toDisplayString(match.text), 1));
                                                                            }), 128))
                                                                        ]),
                                                                        _: 2
                                                                    }, 1024))
                                                                    : (_openBlock(), _createElementBlock("span", _hoisted_4, "0"))
                                                            ], 64))
                                                            : _createCommentVNode("", true)
                                                    ]),
                                                    [`item.sizeBytes`]: _withCtx(({ item }) => [
                                                        _createTextVNode(_toDisplayString(_unref(formatFileSize)(item.sizeBytes)), 1)
                                                    ]),
                                                    [`item.preview`]: _withCtx(({ item }) => [
                                                        _createVNode(_component_v_btn, {
                                                            icon: "mdi-open-in-new",
                                                            variant: "text",
                                                            size: "small",
                                                            loading: previewingId.value === item.id,
                                                            "aria-label": `Preview ${describeRecord(item)}`,
                                                            onClick: ($event) => (onPreview(item))
                                                        }, null, 8, ["loading", "aria-label", "onClick"])
                                                    ]),
                                                    _: 2
                                                }, 1032, ["model-value", "items", "search", "loading"])
                                            ], 64))
                                            : (isEmpty.value)
                                                ? (_openBlock(), _createBlock(_component_v_alert, {
                                                    key: 6,
                                                    type: "info",
                                                    variant: "tonal",
                                                    density: "compact",
                                                    text: "No records found on the network for this patient."
                                                }))
                                                : _createCommentVNode("", true)
                                ]),
                                _: 1
                            }),
                            _createVNode(_component_v_card_actions, { class: "px-6 py-4" }, {
                                default: _withCtx(() => [
                                    _createElementVNode("span", _hoisted_5, _toDisplayString(selectedIds.value.length) + " of " + _toDisplayString(records.value.length) + " " + _toDisplayString(_unref(pluralize)(records.value.length, "record")) + " selected ", 1),
                                    _createVNode(_component_v_spacer),
                                    _createVNode(_component_v_btn_lh, {
                                        loading: saving.value,
                                        onClick: _cache[2] || (_cache[2] = ($event) => (emit('update:modelValue', false)))
                                    }, {
                                        default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                                                _createTextVNode("Done", -1)
                                            ]))]),
                                        _: 1
                                    }, 8, ["loading"])
                                ]),
                                _: 1
                            })
                        ]),
                        _: 1
                    })
                ]),
                _: 1
            }, 8, ["model-value"]));
        };
    }
});
